// frontend/src/components/InstallInstructionsDialog.js

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';

import { isIosDevice } from '../utils/pwaDisplayMode';

/**
 * Platform test is IMPORTED, never re-implemented here. PushEnableNudge decides
 * whether to offer "install the app first" using the same predicate, and a
 * dialog that disagreed with the card handed iPadOS users the desktop column
 * layout right after telling them to install. The shared version also covers
 * iPadOS 13+, which the UA string alone cannot.
 */

/**
 * Hebrew is the app-wide default, so the test is for the explicit opt-out -
 * NOT for `=== 'hebrew'`. `localStorage.language` is written ONLY when a user
 * actively flips the language switch (AuthLayout), so an ABSENT key means
 * "never toggled" and must resolve to Hebrew, matching AuthLayout's
 * `useState(true)` and CookieConsentBanner's `!== 'english'`. Do not "tidy"
 * this back to `=== 'hebrew'`: that renders English for every user who never
 * touched the toggle, and this dialog is the fallback install path - on iOS
 * the ONLY one, since Safari never fires `beforeinstallprompt`.
 *
 * Wrapped because a blocked/throwing localStorage (Safari private mode) must
 * not take the dialog down with it; failing to the default is correct here.
 */
const isHebrewPreferred = () => {
  try {
    return localStorage.getItem('language') !== 'english';
  } catch {
    return true;
  }
};

/**
 * Reusable "how to install the PWA" dialog (iOS single-column / desktop
 * two-column). Extracted from PwaAwarenessSnackbar so it can be opened on
 * demand from anywhere — the profession-identified dialog and the dashboard
 * empty-state both reuse it instead of duplicating the step list.
 *
 * Controlled: pass `open` + `onClose`.
 */
const InstallInstructionsDialog = ({ open, onClose }) => {
  const isHebrew = isHebrewPreferred();
  const isRTL = isHebrew;
  const theme = useTheme();
  // On phones the two platform columns no longer sit side-by-side (they get
  // squeezed to ~half width and wrap word-by-word). Stack them vertically.
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // iOS: single column with full step list
  const iosInstructions = {
    title: isHebrew ? 'התקנת האפליקציה על iPhone' : 'Install the App on iPhone',
    steps: isHebrew
      ? [
          'פתח את הדף בדפדפן Safari',
          'לחץ על שלוש הנקודות האופקיות (···) בתחתית המסך',
          'לחץ על "שתף"',
          'לחץ על "הצגת עוד"',
          'לחץ על "הוספה למסך הבית"',
          'לחץ "הוספה" לאישור',
        ]
      : [
          'Open this page in Safari',
          'Tap the three horizontal dots (···) at the bottom',
          'Tap "Share"',
          'Tap "More"',
          'Tap "Add to Home Screen"',
          'Tap "Add" to confirm',
        ],
  };

  // Desktop: two-column layout with full steps per platform
  const desktopColumns = {
    title: isHebrew ? 'התקנת האפליקציה על הטלפון' : 'Install the App on Your Phone',
    iphone: {
      label: 'iPhone',
      steps: isHebrew
        ? [
            'פתח את הדף בדפדפן Safari',
            'לחץ על שלוש הנקודות האופקיות (···)',
            'לחץ על "שתף"',
            'לחץ על "הצגת עוד"',
            'לחץ על "הוספה למסך הבית"',
            'לחץ "הוספה" לאישור',
          ]
        : [
            'Open this page in Safari',
            'Tap the three horizontal dots (···)',
            'Tap "Share"',
            'Tap "More"',
            'Tap "Add to Home Screen"',
            'Tap "Add" to confirm',
          ],
    },
    android: {
      label: 'Android',
      steps: isHebrew
        ? [
            'פתח את הדף בדפדפן Chrome',
            'לחץ על תפריט (⋮)',
            'לחץ על "הוסף למסך הבית"',
            'לחץ "התקנה" לאישור',
          ]
        : [
            'Open this page in Chrome',
            'Tap the menu (⋮)',
            'Tap "Add to Home Screen"',
            'Tap "Install" to confirm',
          ],
    },
  };

  const stepListSx = { pl: isRTL ? 0 : 2, pr: isRTL ? 2 : 0, m: 0 };
  const stepSx = { mb: 1.5, color: '#1f2937', lineHeight: 1.8, fontWeight: 500, fontSize: '0.9rem' };

  const renderColumn = (col, bgColor) => (
    <Box sx={{ flex: 1, background: bgColor, borderRadius: '12px', p: 2, minWidth: 0 }}>
      <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#0f766e', mb: 1.5 }}>
        {col.label}
      </Typography>
      <Box component="ol" sx={stepListSx}>
        {col.steps.map((step, i) => (
          <Typography key={i} component="li" variant="body2" sx={stepSx}>
            {step}
          </Typography>
        ))}
      </Box>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      dir={isRTL ? 'rtl' : 'ltr'}
      fullWidth
      PaperProps={{
        sx: { borderRadius: '16px', maxWidth: { xs: 440, md: 640 }, width: '100%', px: 1, py: 0.5 },
      }}
    >
      <DialogTitle sx={{ fontWeight: 700, fontSize: '1.1rem', pb: 1 }}>
        {isIosDevice() ? iosInstructions.title : desktopColumns.title}
      </DialogTitle>

      <DialogContent>
        {isIosDevice() ? (
          <Box component="ol" sx={stepListSx}>
            {iosInstructions.steps.map((step, i) => (
              <Typography key={i} component="li" variant="body2" sx={stepSx}>
                {step}
              </Typography>
            ))}
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: isMobile ? 1.25 : 0,
              alignItems: 'stretch',
            }}
          >
            {renderColumn(desktopColumns.iphone, '#f4fbfb')}
            <Divider
              orientation={isMobile ? 'horizontal' : 'vertical'}
              flexItem
              sx={{
                ...(isMobile ? { my: 0 } : { mx: 1.5 }),
                borderColor: 'rgba(13,148,136,0.15)',
              }}
            />
            {renderColumn(desktopColumns.android, '#f4fbfb')}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, pb: 2, justifyContent: 'center' }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            px: 2.5,
            py: 1.25,
            borderRadius: 1.5,
            background: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)',
            color: 'white',
            fontWeight: 500,
            fontSize: '0.875rem',
            boxShadow: 2,
            border: '1px solid rgba(20, 184, 166, 0.5)',
            textTransform: 'none',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'scale(1.05)',
              boxShadow: 4,
              background: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)',
            },
          }}
        >
          {isHebrew ? 'הבנתי' : 'Got it'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InstallInstructionsDialog;
